import React from 'react';

import { Formik, FormikHelpers } from 'formik';

import { observer } from 'mobx-react';

import { useNavigate } from 'react-router-dom';

import { reverse } from 'named-urls';

import dayjs from 'dayjs';

import { isAxiosError } from 'axios';

import Record from 'ui/Record/Record';

import MainHeader from 'components/MainHeader';

import { Button } from 'components/Button';

import { StyledTextField, StyledDatePicker } from 'components/Input/styles';

import EntitySearchField from 'components/EntitySearchField';

import UserAndGroupPicker, {
  GroupResolutionNote,
  PickerValue,
} from 'components/SharedFormComponents/UserAndGroupPicker';

import API from 'utils/api';

import { formErrors, getErrorMessage, isBadRequest } from 'utils/api/errors';

import { useStore } from 'utils/hooks/store';

import { useNavBarHeight } from 'utils/hooks/navbar';

import { UuidableName } from 'utils/api/types';

import { EntityModules } from 'core/constants';

import routes from 'core/routes';

import SendConfirmModal from './components/SendConfirmModal';
import SendResultModal from './components/SendResultModal';
import GroupLicenceNotice from './components/GroupLicenceNotice';

import { AcknowledgementRequestFormValues, initialValues, validationSchema } from './schema';

import {
  buildCreateBody,
  buildPreviewBody,
  estimatedRecipientCount,
  groupsIn,
  mapFieldErrors,
  skippedFrom,
  toFormikErrors,
} from './utils';

import { SendError, SendResult } from './types';

import {
  FormPage,
  FormCard,
  FormSection,
  FormSectionTitle,
  FieldRow,
  FieldLabel,
  RequiredIndicator,
  FieldError,
  FormActions,
  NoteRow,
} from './styles';

const SNAPSHOT_NOTE =
  "Group members are captured when you send. People who join a group later won't be added to this " +
  'request, and people who leave keep their pending acknowledgement.';

const DOCUMENT_SEARCH_PARAMS = { published_only: true };

const AcknowledgementRequestNew: React.FunctionComponent = (): React.ReactElement => {
  const navBarHeight = useNavBarHeight();
  const navigate = useNavigate();
  const { notification, session } = useStore();
  const groupsEnabled = !!session.user?.groupsEnabled;

  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const [sendResult, setSendResult] = React.useState<SendResult | null>(null);
  const [sendError, setSendError] = React.useState<SendError | null>(null);
  // Exact deduped count from the server; null while it's being fetched or if the fetch failed.
  const [previewCount, setPreviewCount] = React.useState<number | null>(null);
  const [previewLoading, setPreviewLoading] = React.useState(false);

  const previewRequest = React.useRef(0);

  React.useEffect(
    () => () => {
      previewRequest.current += 1;
    },
    []
  );

  const submitRequest = async (
    values: AcknowledgementRequestFormValues,
    helpers: FormikHelpers<AcknowledgementRequestFormValues>
  ): Promise<void> => {
    if (!values.document || !values.dueDate) return;
    setSending(true);
    try {
      const { data } = await API.entities.createAcknowledgementRequest(
        buildCreateBody({
          name: values.name,
          document: values.document,
          dueDate: values.dueDate,
          recipients: values.recipients,
        })
      )();
      const recipientCount = data.totals?.total ?? values.recipients.length;
      const skipped = data.skipped ?? [];
      setConfirmOpen(false);
      if (skipped.length > 0) {
        setSendResult({ total: recipientCount, skipped, requestUuid: data.uuid });
        return;
      }
      notification.enqueueSuccessSnackbar(`Request sent · ${recipientCount} actions created`);
      navigate(routes.dashboard.documents.acknowledgementRequests.toString());
    } catch (err) {
      if (isAxiosError(err) && isBadRequest(err)) {
        const fieldErrors = mapFieldErrors(formErrors(err).fieldErrors);
        const skipped = skippedFrom(err.response?.data);
        if (skipped.length > 0) {
          // Resolved to nobody: nothing was created, so this is the one branch that leaves the
          // confirm modal open and reports there rather than closing it.
          setSendError({
            message: fieldErrors.recipients?.join('\n') || 'No eligible recipients in this selection.',
            skipped,
          });
          return;
        }
        setConfirmOpen(false);
        if (Object.keys(fieldErrors).length > 0) {
          helpers.setErrors(toFormikErrors(fieldErrors));
        } else {
          notification.enqueueErrorSnackbar(
            getErrorMessage(err, 'Could not create the request. Please review the form and try again.')
          );
        }
      } else {
        setConfirmOpen(false);
        notification.enqueueErrorSnackbar('Could not create the request. Please try again.');
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <Record
      navBarHeight={navBarHeight}
      Header={
        <MainHeader
          title="New request"
          breadcrumbs={[
            { label: 'Documents', link: routes.dashboard.documents.toString() },
            {
              label: 'Acknowledgements',
              link: routes.dashboard.documents.acknowledgementRequests.toString(),
            },
            {
              label: 'New request',
              link: routes.dashboard.documents.acknowledgementRequests.new.toString(),
            },
          ]}
        />
      }
      Content={() => (
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={async () => {
            // Submission is gated by the Send-confirm modal; opening it
            // is the only path to submitRequest. Formik onSubmit is a no-op.
          }}
        >
          {({ values, errors, touched, setFieldValue, setFieldTouched, validateForm, setErrors, setSubmitting }) => {
            const handleOpenConfirm = async (): Promise<void> => {
              const result = await validateForm();
              setFieldTouched('name', true, false);
              setFieldTouched('document', true, false);
              setFieldTouched('recipients', true, false);
              setFieldTouched('dueDate', true, false);
              if (Object.keys(result).length > 0) {
                return;
              }
              // Cleared on open, not at send-start, so a re-opened modal never shows the previous
              // attempt's error.
              setSendError(null);
              setPreviewCount(null);
              setConfirmOpen(true);

              previewRequest.current += 1;
              const requestId = previewRequest.current;

              // Groups can overlap; only the server can dedupe them to an exact count. Individual-only
              // sends are already exact, so skip the round-trip. A failed fetch falls back to the estimate.
              if (groupsIn(values.recipients).length > 0) {
                setPreviewLoading(true);
                try {
                  const { data } = await API.entities.previewAcknowledgementRecipients(
                    buildPreviewBody(values.recipients)
                  )();
                  if (previewRequest.current === requestId) setPreviewCount(data.total);
                } catch {
                  if (previewRequest.current === requestId) setPreviewCount(null);
                } finally {
                  if (previewRequest.current === requestId) setPreviewLoading(false);
                }
              }
            };

            const handleCloseConfirm = (): void => {
              previewRequest.current += 1;
              setPreviewLoading(false);
              setConfirmOpen(false);
            };

            const handleConfirm = async (): Promise<void> => {
              await submitRequest(values, {
                setErrors,
                setSubmitting,
              } as unknown as FormikHelpers<AcknowledgementRequestFormValues>);
            };

            const selectedGroups = groupsIn(values.recipients);
            const hasGroups = selectedGroups.length > 0;
            // No groups → the individual count is already exact. With groups, prefer the server's
            // deduped total; fall back to the local (over-)estimate only if that fetch failed.
            const modalLoading = hasGroups && previewLoading;
            const modalIsEstimate = hasGroups && !previewLoading && previewCount === null;
            let modalCount = values.recipients.length;
            if (previewCount !== null) {
              modalCount = previewCount;
            } else if (modalIsEstimate) {
              modalCount = estimatedRecipientCount(values.recipients);
            }

            return (
              <FormPage>
                <FormCard onSubmit={(e) => e.preventDefault()}>
                  <FormSection>
                    <FormSectionTitle>Request details</FormSectionTitle>
                    <FieldRow>
                      <FieldLabel htmlFor="name">
                        Name
                        <RequiredIndicator>*</RequiredIndicator>
                      </FieldLabel>
                      <StyledTextField
                        id="name"
                        fullWidth
                        value={values.name}
                        placeholder="Name"
                        onChange={(e) => setFieldValue('name', e.target.value)}
                        onBlur={() => setFieldTouched('name', true)}
                        error={touched.name && !!errors.name}
                        helperText={touched.name && errors.name ? errors.name : undefined}
                      />
                    </FieldRow>

                    <FieldRow>
                      <FieldLabel htmlFor="document">
                        Document
                        <RequiredIndicator>*</RequiredIndicator>
                      </FieldLabel>
                      <EntitySearchField
                        field="document"
                        module={EntityModules.Documents}
                        data={values.document}
                        multiple={false}
                        error={touched.document && !!errors.document}
                        extraParams={DOCUMENT_SEARCH_PARAMS}
                        updateField={(_, value) => {
                          setFieldValue('document', (value as UuidableName) || null);
                          setFieldTouched('document', true, false);
                        }}
                      />
                      {touched.document && errors.document ? <FieldError>{String(errors.document)}</FieldError> : null}
                    </FieldRow>

                    <FieldRow>
                      <FieldLabel htmlFor="recipients">
                        Recipients
                        <RequiredIndicator>*</RequiredIndicator>
                      </FieldLabel>
                      <UserAndGroupPicker
                        id="recipients"
                        value={values.recipients}
                        onChange={(next: PickerValue[]) => setFieldValue('recipients', next)}
                        enableGroups={groupsEnabled}
                        placeholder="Select recipients"
                        groupsPlaceholder="Add users or groups"
                        licenses="documents"
                        // The 400's message stays in the confirm modal, but the border survives Back.
                        error={(touched.recipients && !!errors.recipients) || sendError !== null}
                        helperText={touched.recipients && errors.recipients ? String(errors.recipients) : undefined}
                      />
                      {selectedGroups.length > 0 ? (
                        <NoteRow>
                          <GroupResolutionNote>{SNAPSHOT_NOTE}</GroupResolutionNote>
                        </NoteRow>
                      ) : null}
                      <GroupLicenceNotice />
                    </FieldRow>

                    <FieldRow>
                      <FieldLabel htmlFor="dueDate">
                        Due date
                        <RequiredIndicator>*</RequiredIndicator>
                      </FieldLabel>
                      <StyledDatePicker
                        value={values.dueDate ? dayjs(values.dueDate) : null}
                        minDate={dayjs()}
                        onChange={(value) => {
                          setFieldValue('dueDate', value ? value.toISOString() : null);
                          setFieldTouched('dueDate', true, false);
                        }}
                        slotProps={{
                          textField: {
                            id: 'dueDate',
                            fullWidth: true,
                            error: touched.dueDate && !!errors.dueDate,
                            helperText: touched.dueDate && errors.dueDate ? String(errors.dueDate) : undefined,
                          },
                        }}
                        format="DD/MM/YYYY"
                      />
                    </FieldRow>
                  </FormSection>

                  <FormActions>
                    <Button
                      variant="outlined"
                      size="large"
                      onClick={() => navigate(routes.dashboard.documents.acknowledgementRequests.toString())}
                    >
                      Cancel
                    </Button>
                    <Button variant="contained" size="large" onClick={handleOpenConfirm}>
                      Send
                    </Button>
                  </FormActions>
                </FormCard>

                <SendConfirmModal
                  open={confirmOpen}
                  sending={sending}
                  dueDate={values.dueDate}
                  recipientCount={modalCount}
                  countLoading={modalLoading}
                  isEstimate={modalIsEstimate}
                  sendError={sendError}
                  onClose={handleCloseConfirm}
                  onConfirm={handleConfirm}
                />

                <SendResultModal
                  open={sendResult !== null}
                  total={sendResult?.total ?? 0}
                  skipped={sendResult?.skipped ?? []}
                  onContinue={() =>
                    navigate(
                      reverse(routes.dashboard.documents.acknowledgementRequests.details, {
                        requestId: sendResult?.requestUuid as string,
                      })
                    )
                  }
                />
              </FormPage>
            );
          }}
        </Formik>
      )}
    />
  );
};

export default observer(AcknowledgementRequestNew);
